// Display-only grouping/ordering of parcel owners. A married couple is marked ONLY when
// the wypis itself states the share is held in marital community: the AI extraction fills
// `marital_group` with the same value for both spouses (see wypisy/extraction.py), apply
// persists it on ParcelOwner, and this module merely groups equal non-empty values within
// one parcel. Nothing is inferred from surnames or equal shares.

export interface OwnerDisplayLike {
  id: string;
  full_name: string;
  ownership_share: string;
  /** Udział trzymany samodzielnie OBOK wspólności małżeńskiej (rzadkie, ale legalne:
   *  wypis wykazuje tę samą osobę raz samą i raz w małżeństwie). */
  own_share?: string | null;
  marital_group?: string | null;
}

export interface OwnerDisplayGroup<T extends OwnerDisplayLike> {
  /** One owner, or the spouses of one marital-community share. */
  owners: T[];
  married: boolean;
  /** Share cast to a number for ordering; null when unparseable. */
  shareValue: number | null;
}

/** "96/180" → 0.5(3); "1" → 1; unparseable → null. */
export function parseShareValue(share: string | null | undefined): number | null {
  const s = (share ?? "").trim();
  if (!s) return null;
  const frac = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) {
    const den = Number(frac[2]);
    return den > 0 ? Number(frac[1]) / den : null;
  }
  return /^\d+([.,]\d+)?$/.test(s) ? Number(s.replace(",", ".")) : null;
}

/** Groups spouses (same non-empty `marital_group`) and orders groups by share, largest
 *  first. Spouses stay adjacent and count their joint share once; owners with no
 *  parseable share sort last; ties break alphabetically (PL collation). */
export function groupOwnersForDisplay<T extends OwnerDisplayLike>(owners: T[]): OwnerDisplayGroup<T>[] {
  const byGroup = new Map<string, T[]>();
  for (const o of owners) {
    const key = (o.marital_group ?? "").trim();
    if (key) byGroup.set(key, [...(byGroup.get(key) ?? []), o]);
  }
  const groups: OwnerDisplayGroup<T>[] = [];
  const emitted = new Set<string>();
  for (const o of owners) {
    const key = (o.marital_group ?? "").trim();
    const members = key ? byGroup.get(key)! : [o];
    if (key) {
      if (emitted.has(key)) continue;
      emitted.add(key);
    }
    // Do sortowania grupa reprezentuje wspólny udział + udziały samodzielne członków
    // (osoba może trzymać udział sama ORAZ drugi w małżeństwie — podpis i wiersz są
    // jedne, ale reprezentowany udział to suma).
    const base = parseShareValue(members[0].ownership_share);
    const ownParts = members
      .map((m) => parseShareValue(m.own_share ?? ""))
      .filter((v): v is number => v !== null);
    groups.push({
      owners: members,
      married: members.length > 1,
      shareValue: base === null ? null : ownParts.reduce((a, b) => a + b, base),
    });
  }
  return groups.sort(
    (g1, g2) =>
      (g2.shareValue ?? -1) - (g1.shareValue ?? -1) ||
      g1.owners[0].full_name.localeCompare(g2.owners[0].full_name, "pl"),
  );
}
